import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";

// Importing images/icons
import ArrowRight from "../assets/images/sidebar-icon/arrow-right.svg";
import ArrowRightActive from "../assets/images/sidebar-icon/arrow-right-active.svg";
import Useredit from "../assets/images/sidebar-icon/user-edit.svg";
import UsereditActive from "../assets/images/sidebar-icon/user-edit-active.svg";
import UserSubscription from "../assets/images/sidebar-icon/user-subscription.svg";
import UserSubscriptionActive from "../assets/images/sidebar-icon/user-subscription-active.svg";
import Swap from "../assets/images/sidebar-icon/Swap.svg";
import SwapActive from "../assets/images/sidebar-icon/Swap-active.svg";
import Lock from "../assets/images/sidebar-icon/Lock.svg";
import LockActive from "../assets/images/sidebar-icon/Lock-active.svg";
import Helpicon from "../assets/images/sidebar-icon/message-question.svg";
import HelpiconActive from "../assets/images/sidebar-icon/message-question-active.svg";
import folderIcon from "../assets/images/material-symbols_folder-copy-outline-rounded.svg";
import PaymentEditIcon from "../assets/images/sidebar-icon/update-payment.svg";
import PaymentEditActiveIcon from "../assets/images/sidebar-icon/update-payment-active.svg";
import Logout from "../assets/images/sidebar-icon/Logout.svg";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutPopup from "./LogoutPopup";
import { useDispatch } from "react-redux";
import { clearAuthData } from "../redux/authSlice";
import { clearProfileData } from "../redux/profileSlice";
import { clearAccountContract } from "../features/capabilities/capabilitySlice";
import { beginLogout, finishLogout, revokeSession } from "../helpers/apiClient";
import { clearOnboardingTokens } from "../features/onboarding/api";
import { clearFlowState } from "../features/onboarding/state/persistence";

const sidebarMenuItems = [
  {
    icon: Useredit,
    activeIcon: UsereditActive,
    name: "Edit Profile",
    url: "/profile/edit-profile",
  },
  {
    icon: UserSubscription,
    activeIcon: UserSubscriptionActive,
    name: "Subscription",
    url: "/profile/edit-subscription",
  },
  {
    icon: folderIcon,
    activeIcon: folderIcon,
    name: "Content Retention",
    url: "/profile/content-retention",
  },
  {
    icon: PaymentEditIcon,
    activeIcon: PaymentEditActiveIcon,
    name: "Payment Details",
    url: "/profile/payment-details",
  },
  {
    icon: Swap,
    activeIcon: SwapActive,
    name: "Past Purchases",
    url: "/profile/transactions",
  },
  {
    icon: Lock,
    activeIcon: LockActive,
    name: "Change Password",
    url: "/profile/change-password",
  },
  {
    icon: Helpicon,
    activeIcon: HelpiconActive,
    name: "Help & Support",
    url: "/profile/help-support",
  },
];

const LOGOUT_TIMEOUT_MS = 5000;

function SidebarContent() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false); // State to manage the logout popup visibility
  const logoutInFlight = useRef(false);
  const role = localStorage.getItem("role");
  // Opens the logout popup
  const handleLogoutOpen = () => {
    setLogoutDialogOpen(true);
  };

  // Closes the logout popup
  const handleLogoutClose = () => {
    setLogoutDialogOpen(false);
  };

  const handleConfirmLogout = async () => {
    if (logoutInFlight.current) return;
    logoutInFlight.current = true;

    setLogoutDialogOpen(false);
    let timeoutId;
    try {
      const { accessToken, refreshToken } = await beginLogout();
      if (refreshToken) {
        const controller = new AbortController();
        timeoutId = window.setTimeout(
          () => controller.abort(),
          LOGOUT_TIMEOUT_MS,
        );
        await revokeSession({
          refreshToken,
          accessToken,
          signal: controller.signal,
        });
      }
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Logout request failed:", error);
      }
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
      dispatch(clearAuthData());
      dispatch(clearProfileData());
      // So the next member signing in to this tab never inherits the last one's
      // capabilities (ECH-288).
      dispatch(clearAccountContract());
      localStorage.removeItem("loginDetails");
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("interests");
      localStorage.removeItem("expertise");
      clearOnboardingTokens();
      clearFlowState();
      finishLogout();
      navigate("/login", { replace: true });
    }
  };

  const filteredSidebarMenuItems =
    role !== "sponsor"
      ? sidebarMenuItems.filter((item) => item.name !== "Content Retention")
      : sidebarMenuItems;
  return (
    <Box>
      {/* Sidebar Content */}
      {filteredSidebarMenuItems.map((item) => {
        const isActive = location.pathname?.includes(item.url);
        return (
          <>
            <Button
              onClick={() => navigate(item.url)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                textTransform: "none",
                padding: {
                  xs: "5px 10px !important",
                  sm: "5px 10px !important",
                  md: "5px 10px !important",
                  lg: "5px 20px !important",
                },
                marginBottom: "5px",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: {
                    xs: "5px",
                    sm: "10px",
                  },
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: {
                      xs: "25px",
                      sm: "34px",
                    },
                    height: {
                      xs: "25px",
                      sm: "34px",
                    },
                    backgroundColor: "#F1F7FA",
                    borderRadius: "50%",
                  }}
                >
                  <img
                    src={isActive ? item.activeIcon : item.icon}
                    width="20px"
                    height="20px"
                    alt={item.icon}
                    className="profile-dropdown-img"
                  />
                </Box>
                <Typography
                  sx={
                    isActive
                      ? {
                          fontWeight: "700",
                          fontSize: "13.5px",
                          color: "#2A4876",
                        }
                      : { color: "#000", fontWeight: "500", fontSize: "13.5px" }
                  }
                >
                  {item?.name}
                </Typography>
              </Box>
              <img
                style={{ marginLeft: "15px" }}
                src={isActive ? ArrowRightActive : ArrowRight}
                width={8}
                alt="Chevron"
                className="arrow-icon"
              />
            </Button>
          </>
        );
      })}
      <Button
        onClick={handleLogoutOpen} // Opens the logout popup
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textTransform: "none",
          padding: {
            xs: "5px 10px !important",
            sm: "5px 10px !important",
            md: "5px 10px !important",
            lg: "5px 20px !important",
          },
          marginBottom: "5px",
          width: "100%",
          "& .MuiButton-endIcon": {
            margin: 0,
          },
        }}
        endIcon={
          <img
            style={{ marginLeft: "15px" }}
            src={ArrowRight}
            width={8}
            alt="Chevron"
            className="arrow-icon"
          />
        }
      >
        <Box
          sx={{
            display: "flex",
            gap: {
              xs: "5px",
              sm: "10px",
            },
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: {
                xs: "25px",
                sm: "34px",
              },
              height: {
                xs: "25px",
                sm: "34px",
              },
              backgroundColor: "#F1F7FA",
              borderRadius: "50%",
            }}
          >
            <img
              src={Logout}
              className="profile-dropdown-img"
              width="20px"
              height="20px"
              alt="Logout icon"
            />
          </Box>
          <Typography
            sx={{ color: "#000", fontWeight: "500", fontSize: "13.5px" }}
          >
            Logout
          </Typography>
        </Box>
      </Button>
      {/* Logout Popup Dialog */}
      <LogoutPopup
        logoutDialogOpen={logoutDialogOpen}
        handleConfirmLogout={handleConfirmLogout}
        handleLogoutClose={handleLogoutClose}
        handleLogoutOpen={handleLogoutOpen}
      />
    </Box>
  );
}

export default SidebarContent;
